import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ClipboardList, CheckCircle2, XCircle, ChevronDown, Users,
  CalendarPlus, AlertOctagon, RefreshCw, ArrowLeft, GitMerge,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * EtimeofficeQueuePage
 * --------------------
 * Approval queue for E-TIMEOFFICE sync. Three kinds of pending actions:
 *   - empcode_mapping     — match a machine Empcode to a Girgit employee
 *   - comp_off_credit     — credit a comp-off for working a weekend/holiday
 *   - unauthorized_absence — flag a no-show without an approved leave
 *
 * NOTHING is applied to live data until an admin clicks Approve.
 */
const EtimeofficeQueuePage = () => {
  const [tab, setTab] = useState('empcode_mapping');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ empcode_mapping: 0, comp_off_credit: 0, unauthorized_absence: 0, reconciliation_conflict: 0 });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('pending');

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('adminToken')}` });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/etimeoffice/pending-actions`, {
        headers: auth(),
        params: { kind: tab, status },
      });
      setItems(r.data.items || []);
      setCounts(r.data.pending_counts || counts);
    } catch (e) {
      toast.error('Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, [tab, status]);

  useEffect(() => { load(); }, [load]);

  const decide = async (actionId, decision, payload = {}) => {
    try {
      await axios.post(`${API}/api/admin/etimeoffice/pending-actions/${actionId}/decide`, {
        decision,
        ...payload,
      }, { headers: auth() });
      toast.success(decision === 'approve' ? 'Approved ✓' : 'Rejected');
      load();
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="etimeoffice-queue-page">
      <header className="mb-5">
        <a href="/admin/dashboard?page=etimeoffice" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="w-3 h-3" /> Back to settings
        </a>
        <div className="flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-amber-600" />
          <h1 className="text-2xl font-semibold text-gray-900">Approval Queue</h1>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Every change suggested by the attendance machine waits here for your
          approval. Nothing is committed to employee records until you click
          <strong className="mx-1">Approve</strong>.
        </p>
      </header>

      {/* Status filter */}
      <div className="mb-4 flex items-center gap-2 text-sm">
        <span className="text-gray-500">Show:</span>
        {['pending', 'approved', 'rejected', 'all'].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`queue-status-${s}`}
            className={`px-2.5 py-1 rounded-md text-xs ${
              status === s
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {s}
          </button>
        ))}
        <button onClick={load} className="ml-auto text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="flex gap-1" aria-label="Queue tabs">
          <TabButton
            id="empcode_mapping"
            label="Employee mappings"
            icon={Users}
            count={counts.empcode_mapping}
            active={tab === 'empcode_mapping'}
            onClick={() => setTab('empcode_mapping')}
          />
          <TabButton
            id="comp_off_credit"
            label="Comp-off credits"
            icon={CalendarPlus}
            count={counts.comp_off_credit}
            active={tab === 'comp_off_credit'}
            onClick={() => setTab('comp_off_credit')}
          />
          <TabButton
            id="unauthorized_absence"
            label="Unauthorised absences"
            icon={AlertOctagon}
            count={counts.unauthorized_absence}
            active={tab === 'unauthorized_absence'}
            onClick={() => setTab('unauthorized_absence')}
          />
          <TabButton
            id="reconciliation_conflict"
            label="Reconciliation conflicts"
            icon={GitMerge}
            count={counts.reconciliation_conflict}
            active={tab === 'reconciliation_conflict'}
            onClick={() => setTab('reconciliation_conflict')}
          />
        </nav>
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <Empty status={status} tab={tab} />
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            if (item.kind === 'empcode_mapping') return <MappingCard key={item._id} item={item} onDecide={decide} />;
            if (item.kind === 'comp_off_credit') return <CompOffCard key={item._id} item={item} onDecide={decide} />;
            if (item.kind === 'unauthorized_absence') return <AbsenceCard key={item._id} item={item} onDecide={decide} />;
            if (item.kind === 'reconciliation_conflict') return <ConflictCard key={item._id} item={item} onDecide={decide} />;
            return null;
          })}
        </div>
      )}
    </div>
  );
};

// ─── Empty state ───────────────────────────────────────────────────────────

const Empty = ({ status, tab }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-10 text-center" data-testid="queue-empty">
    <CheckCircle2 className="w-10 h-10 mx-auto text-green-500 mb-3" />
    <h3 className="font-medium text-gray-900 mb-1">Queue is clear</h3>
    <p className="text-sm text-gray-500">
      No {status === 'pending' ? '' : status} {labelFor(tab)} right now. Run a
      sync from Settings to fetch new data.
    </p>
  </div>
);

const labelFor = (k) => ({
  empcode_mapping: 'mappings',
  comp_off_credit: 'comp-off credits',
  unauthorized_absence: 'unauthorised absences',
  reconciliation_conflict: 'reconciliation conflicts',
}[k] || 'actions');

// ─── Tabs ──────────────────────────────────────────────────────────────────

const TabButton = ({ id, label, icon: Icon, count, active, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`queue-tab-${id}`}
    className={`px-3 py-2 text-sm rounded-t-md inline-flex items-center gap-2 border-b-2 -mb-px ${
      active ? 'border-blue-600 text-blue-700 bg-blue-50/50' : 'border-transparent text-gray-600 hover:text-gray-800'
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
    {count > 0 && (
      <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
        active ? 'bg-blue-600 text-white' : 'bg-amber-500 text-white'
      }`}>{count}</span>
    )}
  </button>
);

// ─── Action cards ──────────────────────────────────────────────────────────

const MappingCard = ({ item, onDecide }) => {
  const p = item.payload || {};
  const [pickerOpen, setPickerOpen] = useState(false);
  const [override, setOverride] = useState(p.suggested_employee_id || '');
  const isPending = item.status === 'pending';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`mapping-card-${item._id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">{p.empcode}</span>
            <span className="text-sm text-gray-500">·</span>
            <span className="text-sm text-gray-900 font-medium">{p.api_name || '(no name)'}</span>
          </div>
          <div className="text-xs text-gray-500">From machine. Suggested match below.</div>
        </div>
        <Status status={item.status} />
      </div>

      <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-md text-sm">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-blue-900">Suggested: </span>
            <strong className="text-blue-900">{p.suggested_employee_name || '— no match found —'}</strong>
            {p.confidence ? (
              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                p.confidence >= 90 ? 'bg-green-100 text-green-800' :
                p.confidence >= 70 ? 'bg-amber-100 text-amber-800' :
                'bg-red-100 text-red-800'
              }`}>{p.confidence}% confidence</span>
            ) : null}
          </div>
          {(p.alternatives?.length > 0 || !p.suggested_employee_id) && (
            <button
              onClick={() => setPickerOpen((v) => !v)}
              data-testid={`mapping-pick-${item._id}`}
              className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1"
            >
              Pick different <ChevronDown className="w-3 h-3" />
            </button>
          )}
        </div>
        {pickerOpen && (
          <EmployeePicker selected={override} onChange={setOverride} alternatives={p.alternatives} />
        )}
      </div>

      {isPending && (
        <div className="mt-3 flex gap-2 justify-end">
          <button
            onClick={() => onDecide(item._id, 'reject', { reason: 'not_our_employee' })}
            data-testid={`mapping-reject-${item._id}`}
            className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            Not our employee
          </button>
          <button
            onClick={() => onDecide(item._id, 'approve', { override_employee_id: override })}
            disabled={!override}
            data-testid={`mapping-approve-${item._id}`}
            className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Approve mapping
          </button>
        </div>
      )}
    </div>
  );
};

const CompOffCard = ({ item, onDecide }) => {
  const p = item.payload || {};
  const isPending = item.status === 'pending';
  const hours = p.work_minutes ? (p.work_minutes / 60).toFixed(1) : null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`compoff-card-${item._id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <CalendarPlus className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-gray-900">{p.name || `Empcode ${p.empcode}`}</span>
          </div>
          <p className="text-sm text-gray-600">
            Worked on <strong>{p.earned_on}</strong>
            {p.is_weekend && <span className="ml-1 text-xs bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded">weekend</span>}
            {p.is_holiday && <span className="ml-1 text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">holiday</span>}
            {hours && <span className="ml-2 text-xs text-gray-500">· {hours}h logged</span>}
          </p>
          <p className="text-xs text-gray-500 mt-1">Approving will credit 1 day to the comp-off ledger.</p>
        </div>
        <Status status={item.status} />
      </div>
      {isPending && (
        <div className="mt-3 flex gap-2 justify-end">
          <button onClick={() => onDecide(item._id, 'reject', { reason: 'not_eligible' })}
                  data-testid={`compoff-reject-${item._id}`}
                  className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50">
            Reject
          </button>
          <button onClick={() => onDecide(item._id, 'approve')}
                  data-testid={`compoff-approve-${item._id}`}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> Approve credit
          </button>
        </div>
      )}
    </div>
  );
};

const AbsenceCard = ({ item, onDecide }) => {
  const p = item.payload || {};
  const isPending = item.status === 'pending';
  return (
    <div className="bg-white border border-red-200 rounded-lg p-4 bg-red-50/30" data-testid={`absence-card-${item._id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <AlertOctagon className="w-4 h-4 text-red-600" />
            <span className="text-sm font-medium text-gray-900">{p.name || `Empcode ${p.empcode}`}</span>
          </div>
          <p className="text-sm text-gray-700">
            Absent on <strong>{p.date}</strong> · no approved leave on record.
          </p>
          {p.machine_remark && (
            <p className="text-xs text-gray-500 italic mt-1">Machine remark: &ldquo;{p.machine_remark}&rdquo;</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Approving will record this as an unauthorised absence (visible on Reports).
          </p>
        </div>
        <Status status={item.status} />
      </div>
      {isPending && (
        <div className="mt-3 flex gap-2 justify-end">
          <button onClick={() => onDecide(item._id, 'reject', { reason: 'data_error' })}
                  data-testid={`absence-reject-${item._id}`}
                  className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50">
            Dismiss
          </button>
          <button onClick={() => onDecide(item._id, 'approve')}
                  data-testid={`absence-approve-${item._id}`}
                  className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> Confirm absence
          </button>
        </div>
      )}
    </div>
  );
};

const ConflictCard = ({ item, onDecide }) => {
  const p = item.payload || {};
  const isPending = item.status === 'pending';
  const resolutions = p.resolutions || [];
  const defaultRes = resolutions.find((r) => r.default)?.id || resolutions[0]?.id || '';
  const [picked, setPicked] = useState(defaultRes);

  const labels = {
    leave_full_punch: 'Approved full-day leave + Office punch',
    leave_half_punch_full: 'Half-day leave + Full-day punch',
    leave_half_no_punch: 'Half-day leave + No punch',
    wfh_office_punch: 'WFH approved + Office punch',
    prod_office_punch: 'PROD approved + Office punch',
    machine_a_with_leave: 'Machine marked Absent + Approved leave',
  };
  const hours = p.work_minutes ? (p.work_minutes / 60).toFixed(1) : null;

  return (
    <div className="bg-white border border-amber-200 rounded-lg p-4" data-testid={`conflict-card-${item._id}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <GitMerge className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-gray-900">{p.name || `Empcode ${p.empcode}`}</span>
            <span className="text-xs text-gray-400">·</span>
            <span className="text-sm text-gray-700">{p.date}</span>
          </div>
          <div className="text-sm text-amber-900 font-medium">{labels[p.conflict_type] || p.conflict_type}</div>
          <div className="mt-1 text-xs text-gray-600 flex flex-wrap gap-x-3 gap-y-0.5">
            {p.leave_type && (
              <span>Leave: <strong className="uppercase">{p.leave_type}</strong>{p.is_half_day ? ' (half-day)' : ''}</span>
            )}
            {p.in_time && <span>In: {new Date(p.in_time).toLocaleTimeString('en-IN', {hour:'2-digit',minute:'2-digit'})}</span>}
            {p.out_time && <span>Out: {new Date(p.out_time).toLocaleTimeString('en-IN', {hour:'2-digit',minute:'2-digit'})}</span>}
            {hours && <span>Logged: {hours}h</span>}
            {p.machine_status && <span>Status: <strong>{p.machine_status}</strong></span>}
          </div>
        </div>
        <Status status={item.status} />
      </div>

      {isPending && resolutions.length > 0 && (
        <>
          <div className="text-xs text-gray-700 mb-2 font-medium">How should this be resolved?</div>
          <div className="space-y-1.5 mb-3">
            {resolutions.map((r) => (
              <label
                key={r.id}
                className={`flex items-start gap-2 p-2 rounded-md cursor-pointer border ${
                  picked === r.id ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name={`res-${item._id}`}
                  value={r.id}
                  checked={picked === r.id}
                  onChange={() => setPicked(r.id)}
                  data-testid={`conflict-resolution-${item._id}-${r.id}`}
                  className="mt-0.5"
                />
                <span className="text-sm text-gray-800">
                  {r.label}
                  {r.default && <span className="ml-1 text-[10px] uppercase tracking-wider text-amber-700">recommended</span>}
                </span>
              </label>
            ))}
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => onDecide(item._id, 'reject', { reason: 'reviewed_no_action' })}
              data-testid={`conflict-skip-${item._id}`}
              className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
            >
              Skip (no action)
            </button>
            <button
              onClick={() => onDecide(item._id, 'approve', { reason: picked })}
              disabled={!picked}
              data-testid={`conflict-apply-${item._id}`}
              className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-md hover:bg-amber-700 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Apply resolution
            </button>
          </div>
        </>
      )}
      {!isPending && item.status === 'approved' && (
        <div className="text-xs text-green-700 mt-2">
          Resolved: <strong>{(p.resolutions || []).find((r) => r.id === item.decision_reason)?.label || item.decision_reason}</strong>
        </div>
      )}
    </div>
  );
};

// ─── Employee picker (search by name in current employees) ─────────────────

const EmployeePicker = ({ selected, onChange, alternatives }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('adminToken')}` });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/admin/employees`, { headers: auth() });
        if (!cancelled) {
          setResults((r.data?.employees || r.data || []).filter((e) =>
            (e.employment?.status || '').toLowerCase() === 'active'
          ));
        }
      } catch (e) { /* best-effort */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = results.filter((e) => {
    const n = ((e.personal?.full_name || e.work_email || '') + ' ' + (e.employment?.employee_id || '')).toLowerCase();
    return !q || n.includes(q.toLowerCase());
  }).slice(0, 12);

  return (
    <div className="mt-3 border border-gray-200 rounded-md bg-white p-2">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search employee by name or code"
        data-testid="employee-picker-search"
        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md mb-2"
      />
      {alternatives && alternatives.length > 0 && !q && (
        <div className="text-[10px] uppercase tracking-wider text-gray-400 px-1 mb-1">Other matches</div>
      )}
      <div className="max-h-48 overflow-y-auto">
        {(q ? filtered : (alternatives?.map((a) => ({
          _id: a._id,
          personal: { full_name: a.name },
          employment: {},
          _confidence: a.confidence,
        })) || filtered)).map((e) => (
          <label key={e._id} className={`flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer text-sm ${selected === e._id ? 'bg-blue-50' : ''}`}>
            <input
              type="radio"
              name="emp"
              value={e._id}
              checked={selected === e._id}
              onChange={() => onChange(e._id)}
              data-testid={`emp-pick-${e._id}`}
            />
            <span className="flex-1">
              <span className="font-medium text-gray-900">{e.personal?.full_name || e.work_email}</span>
              {e.employment?.employee_id && <span className="ml-2 text-xs text-gray-500">{e.employment.employee_id}</span>}
            </span>
            {e._confidence && <span className="text-xs text-amber-700">{e._confidence}%</span>}
          </label>
        ))}
      </div>
    </div>
  );
};

const Status = ({ status }) => {
  const styles = {
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-gray-100 text-gray-700',
  };
  const Icon = status === 'approved' ? CheckCircle2 : status === 'rejected' ? XCircle : RefreshCw;
  return (
    <span className={`text-[11px] px-2 py-1 rounded-md inline-flex items-center gap-1 ${styles[status] || styles.pending}`}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
};

export default EtimeofficeQueuePage;
